import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyMenuModule as MatMenuModule, MatLegacyMenuPanel as MatMenuPanel } from '@angular/material/legacy-menu';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { first, map } from 'rxjs/operators';
import { firstValueFrom,of } from 'rxjs';
import { FileEntity } from '../../../dave-data-module/entities/file.entity';
import { FileDataService } from '../../../dave-data-module/services/file-data.service';
import { DownloadVersions, DownloadZip } from '../../../dave-data-module/services/http.service';
import { State } from '../../../dave-data-module/State';
import { getFileById } from '../../../dave-data-module/State/selectors/files.selectors';
import { EmailEditorComponent, EmailEditorComponentDialogData } from '../../../dave-email-module/components/email-editor/email-editor.component';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { ExpansionPanelComponent } from '../../../expansion-panel/expansion-panel.component';
import { InvoiceEditorMeta,ReportsPageMeta } from '../../../helper/page-metadata';
import { MoveFileFolderService } from '../../../services/move-file-folder.service';
import { SmallFileCardModule } from '../../../small-file-card/small-file-card.module';
import { getOriginalFileIds } from '../../../helper/helper';

@Component({
    selector: 'app-document-list-card[FileIds]',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatExpansionModule,
        SmallFileCardModule,
        ExpansionPanelComponent,
        MatBadgeModule,
        FontAwesomeModule,
        AppButtonModule,
        //DaveEmailModule, // verursacht ReferenceError zwischen Modulen (Circular dependency)
        EmailEditorComponent, /// DaveEmailModule  durch EmailEditorComponent(standalone) ersetzt
        MatMenuModule,
        MatTooltipModule,
        //für dialog
    ],
    templateUrl: './document-list-card.component.html',
    styleUrls: ['./document-list-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentListCardComponent {
    protected multiselect = false;
    protected valid_fileIds: number[];
    @Input() set RemoveButton(value) {
        this.removeButton = coerceBooleanProperty(value);
    }
    protected removeButton = false;
    // tslint:disable-next-line: member-ordering naming-convention
    static ngAcceptInputType_RemoveButton: BooleanInput;

    @Input() SortFilesBy?: keyof FileEntity;
    @Input() SortDirection: 'asc' | 'desc' = 'asc';

    @Input() public set FileIds(ids: number[]) {
        if (ids == null) {
            this.valid_fileIds = null;
            this.changeDetectionRef.detectChanges();
            return;
        }
        firstValueFrom(this.fileDataService.GetFilesById(ids)).then((files) => {
            files = this.sortFiles(files);
            this.valid_fileIds = files.map((file) => file.Id);
            this.changeDetectionRef.detectChanges();
        });
    }

    @Input() Expanded = true;
    @Input() Headline = 'Dateien';
    @Input() EmptyText = 'Keine Dateien vorhanden';
    @Input() excludeRemoveButtonFromFiles: number[] = [];

    protected ledgerPath: string;
    @Input() public set ledgerImportPath(path: string) {
        if (path) {
            this.ledgerPath = path;
        }
    }
    @Input() UploadMenu: MatMenuPanel;

    @Input() SelectedFiles: number[] = [];
    @Output() SelectedFilesChange = new EventEmitter<number[]>();

    @Output() FileClick = new EventEmitter<{ file: FileEntity; multiselect: boolean }>();
    @Output() RemoveClick = new EventEmitter<FileEntity>();

    @Output() UploadClick = new EventEmitter<void>();
    @Output() MergePdfClick = new EventEmitter<number[]>();

    constructor(protected fileDataService: FileDataService, protected changeDetectionRef: ChangeDetectorRef, private dialog: MatDialog, private store: Store<State>, private moveFileFolderService: MoveFileFolderService) {}

    getMergePdfDisabled$ = () => {
        const fileIds = this.SelectedFiles;
        if (!fileIds || fileIds.length < 2) {
            return of(true);
        } else {
            return this.fileDataService.GetFilesById(fileIds).pipe(
                first(),
                map((files) => files.some((f) => f?.MIMEType !== 'application/pdf')),
            );
        }
    };
    onFileClick(file: FileEntity) {
        this.FileClick.emit({ file, multiselect: this.multiselect });
        if (this.multiselect) {
            if (this.SelectedFiles.every((id) => id !== file.Id)) {
                this.SelectedFiles.push(file.Id);
                this.SelectedFilesChange.emit(this.SelectedFiles.slice());
                this.changeDetectionRef.detectChanges();
            } else if (this.SelectedFiles.includes(file.Id)) {
                this.SelectedFiles.splice(this.SelectedFiles.indexOf(file.Id), 1);
                this.SelectedFilesChange.emit(this.SelectedFiles.slice());
                this.changeDetectionRef.detectChanges();
            }
        }
    }
    resetMultiselect() {
        this.SelectedFiles = [];
        this.multiselect = false;
    }
    onEmailClick() {
        const FileAttachmntFileIds = this.SelectedFiles.slice();
        if (FileAttachmntFileIds?.length) {
            this.resetMultiselect();
            this.dialog.open<EmailEditorComponent, EmailEditorComponentDialogData>(EmailEditorComponent, {
                ...EmailEditorComponent.DefaultConfig,
                data: {
                    FileAttachmntFileIds,
                },
            });
        }
    }

    async onClickDetectDocuments(): Promise<void> {
        const fileIds = this.SelectedFiles;
        if (!fileIds?.length) {
            return;
        }

        this.resetMultiselect();
        const files = await firstValueFrom(this.fileDataService.GetFilesById(fileIds));

        const versionIds = files.map(file => file.GetLastVersion().Id);

        // Falls Verknüpfungen ausgewählt wurden, werden die Ids der originalen Dateien in das Array geschrieben
        const originalFileIds = getOriginalFileIds(files);

        await this.fileDataService.setDetectDocumentPrio(versionIds, originalFileIds);
    }

    onMergePdfClick() {
        this.MergePdfClick.emit(this.SelectedFiles);
        this.resetMultiselect();
    }

    onFileDownloadClick() {
        if (this.SelectedFiles.length) {
            // DownloadZip()
            // DownloadVersions()
            if (this.SelectedFiles.length == 1) {
                /// wenn nur eine Datei ausgewählt ist, dann holen wir die lätzte Version davon
                firstValueFrom(this.store.select(getFileById({ id: this.SelectedFiles[0] }))).then((document) => {
                    DownloadVersions([document.GetLastVersion()]);
                    //download version
                });
            } else {
                ////  wenn mehrere Dateien ausgewält sind, dann werden sie als zip-datei heruntergeladen
                DownloadZip(this.SelectedFiles, []); // zweite Parameter leeres array bei folderIds muss nicht angegeben werden
            }
        }
    }

    onFileMoveClick() {
        if (this.SelectedFiles.length) {
            this.moveFileFolderService.MoveFileFolder(this.SelectedFiles, []);
            this.resetMultiselect();
        }
    }

    isNotExcludedFromRemove(id: number) {
        return this.excludeRemoveButtonFromFiles ? !this.excludeRemoveButtonFromFiles.includes(id) : true;
    }

    /**
     * Sortiert die Dateien basierend auf der angegebenen Sortieroption.
     * @private
     */
    private sortFiles(files: FileEntity[]): FileEntity[] {
        if (!this.SortFilesBy) return files;
        return files.slice().sort((a, b) => {
            const aValue = a[this.SortFilesBy];
            const bValue = b[this.SortFilesBy];
            if (aValue == null) return 1;
            if (bValue == null) return -1;
            if (aValue === bValue) return 0;
            if (this.SortDirection === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }

    protected readonly ReportsPageMeta = ReportsPageMeta;
    protected readonly InvoiceEditorMeta = InvoiceEditorMeta;
}

